import {CryptojsService} from "../../../shared/cryptojs.service";
import {AdminPushNotification} from "../../../payloads/notification";

export class LocalStorage {

  static readonly LOCAL_STORAGE_KEY_PREFIX = '@SmartDiningRestaurantLocal:';
  static readonly LOCAL_STORAGE_NOTIFICATION_KEY = this.LOCAL_STORAGE_KEY_PREFIX + 'pushNotifications';
  static readonly LOCAL_STORAGE_PERMISSIONS_KEY = this.LOCAL_STORAGE_KEY_PREFIX + 'permissions';

  private static isAvailable(): boolean {
    return typeof localStorage !== 'undefined';
  }


  public static getLocalString(key: string): string {
    if (!this.isAvailable()) {
      return null;
    }
    return localStorage.getItem(this.LOCAL_STORAGE_KEY_PREFIX + key);
  }

  public static getLocalObject(key: string): any {
    if (!this.isAvailable()) {
      return null;
    }
    const raw = localStorage.getItem(this.LOCAL_STORAGE_KEY_PREFIX + key);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  public static setLocalString(key: string, value: string) {
    if (!this.isAvailable()) {
      return;
    }
    localStorage.setItem(this.LOCAL_STORAGE_KEY_PREFIX + key, value);
  }

  public static setLocalObject(key: string, value: any) {
    if (!this.isAvailable()) {
      return;
    }
    localStorage.setItem(
      this.LOCAL_STORAGE_KEY_PREFIX + key,
      JSON.stringify(value)
    );
  }

  public static removeLocal(key: string): void {
    if (!this.isAvailable()) {
      return;
    }
    localStorage.removeItem(this.LOCAL_STORAGE_KEY_PREFIX + key);
  }

  public static resetLocal(): void {
    if (!this.isAvailable()) {
      return;
    }
    localStorage.clear();
  }

  public static getRefreshToken(): string {
    if (!this.isAvailable()) {
      return "";
    }
    const refreshToken =  localStorage.getItem('refreshToken');
    return refreshToken ? CryptojsService.decryptData(refreshToken) : "";
  }

  public static setRefreshToken(token: string) {
    if (!this.isAvailable()) {
      return;
    }
    localStorage.setItem('refreshToken', CryptojsService.encryptData(token));
  }

  public static getToken(): string {
    if (!this.isAvailable()) {
      return "";
    }
    const token =  localStorage.getItem('token');
    return token ? CryptojsService.decryptData(token) : "";
  }

  public static setToken(token: string) {
    if (!this.isAvailable()) {
      return;
    }
    localStorage.setItem('token', CryptojsService.encryptData(token));
  }

  /** Clears access + refresh JWT before a new sign-in so a stale token is not sent on getAuthenticatedUser. */
  public static clearAuthTokens(): void {
    if (!this.isAvailable()) {
      return;
    }
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
  }

  public static existKey(key: string): boolean {
    if (!this.isAvailable()) {
      return false;
    }
    return !!localStorage.getItem(key);
  }

  public static setPushNotifications(data: AdminPushNotification[]) {
    if (!this.isAvailable()) {
      return;
    }
    localStorage.setItem(LocalStorage.LOCAL_STORAGE_NOTIFICATION_KEY, JSON.stringify(data));
  }

  public static getPushNotifications(): AdminPushNotification[] {
    if (!this.isAvailable()) {
      return [];
    }
    if (localStorage.getItem(LocalStorage.LOCAL_STORAGE_NOTIFICATION_KEY)) {
      return JSON.parse(localStorage.getItem(LocalStorage.LOCAL_STORAGE_NOTIFICATION_KEY));
    } else {
      return [];
    }
  }

  public static setPermissions(data: string[]) {
    if (!this.isAvailable()) {
      return;
    }
    localStorage.setItem(LocalStorage.LOCAL_STORAGE_PERMISSIONS_KEY, JSON.stringify(data));
  }

  public static getPermissions(): string[] {
    if (!this.isAvailable()) {
      return [];
    }
    const permissions = localStorage.getItem(LocalStorage.LOCAL_STORAGE_PERMISSIONS_KEY);
    if (permissions) {
      return JSON.parse(permissions);
    } else {
      return [];
    }
  }

  public static getSidenavScrollPos(): string {
    if (!this.isAvailable()) {
      return null;
    }
    return localStorage.getItem('sidenav-scroll-pos');
  }

  public static setSidenavScrollPos(pos: string) {
    if (!this.isAvailable()) {
      return;
    }
    localStorage.setItem('sidenav-scroll-pos', pos);
  }

  public static getSmallSidenavScrollPos(): string {
    if (!this.isAvailable()) {
      return null;
    }
    return localStorage.getItem('small-sidenav-scroll-pos');
  }

  public static setSmallSidenavScrollPos(pos: string) {
    if (!this.isAvailable()) {
      return;
    }
    localStorage.setItem('small-sidenav-scroll-pos', pos);
  }

  public static getWalletPagePrevPathData(): string {
    if (!this.isAvailable()) {
      return null;
    }
    return localStorage.getItem('wallet-page-prevpath-data');
  }

  public static setWalletPagePrevPathData(pos: string) {
    if (!this.isAvailable()) {
      return;
    }
    localStorage.setItem('wallet-page-prevpath-data', pos);
  }

  // Some legacy keys were stored without the standard prefix; keep them isolated here.
  public static getRawString(key: string): string {
    if (!this.isAvailable()) {
      return null;
    }
    return localStorage.getItem(key);
  }

  public static setRawString(key: string, value: string): void {
    if (!this.isAvailable()) {
      return;
    }
    localStorage.setItem(key, value);
  }

}
